import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { registerSW } from "virtual:pwa-register";
import "./styles/fonts.css";
import "./styles/app.css";
import "./styles/additions.css";
import { AuthProvider, useAuth } from "./auth/AuthContext.js";
import { LoginScreen } from "./screens/LoginScreen.js";
import { App } from "./App.js";
import { UpdateBanner, signalNeedRefresh } from "./components/UpdateBanner.js";

// Enregistrement du service worker. Sans effet en développement. À l'arrivée d'une nouvelle version,
// on n'applique pas la mise à jour en silence : on affiche un bandeau et l'utilisateur recharge
// lui-même (updateSW(true) applique le nouveau worker puis recharge).
const updateSW = registerSW({
  onNeedRefresh() {
    signalNeedRefresh(() => void updateSW(true));
  },
  onRegisteredSW(_swUrl, r) {
    if (!r) return;
    // Piège des PWA installées (iOS surtout) : si une nouvelle version a été détectée lors d'une
    // session précédente, le worker reste « en attente » et onNeedRefresh NE se redéclenche PAS au
    // lancement suivant — le bandeau ne réapparaît jamais et l'ancien bundle est servi indéfiniment.
    // On resignale donc explicitement dès qu'un worker en attente est présent au démarrage.
    if (r.waiting) signalNeedRefresh(() => void updateSW(true));
    // iOS ne vérifie pas les mises à jour d'une PWA installée de lui-même. On sollicite une
    // vérification à chaque retour au premier plan (réouverture depuis l'écran d'accueil).
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") void r.update();
    });
  },
});

/** Porte d'authentification : chargement → connexion (anonyme) → application (authentifié). */
function Root() {
  const { status } = useAuth();
  if (status === "loading") {
    return (
      <div className="fullscreen-center">
        <div className="spinner" />
      </div>
    );
  }
  if (status === "anonymous") return <LoginScreen />;
  return <App />;
}

const container = document.getElementById("root");
if (!container) throw new Error("Élément racine #root introuvable.");
createRoot(container).render(
  <StrictMode>
    <AuthProvider>
      <Root />
    </AuthProvider>
    <UpdateBanner />
  </StrictMode>,
);
